import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { PartitionedEventListenerBase } from 'app/calendar/event_listener_base';
import { NewEventComponent } from 'app/calendar/newEvent/newEvent';
import { StyleComponent } from 'app/components/style/style.component';
import { Agenda } from 'app/model/agenda';
import { Agende } from 'app/model/agende';
import { IIntervallo } from 'app/model/ambulatorio';
import { IAppuntamento } from 'app/model/appuntamento';
import * as moment from 'moment';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import { combineLatest, switchMap } from 'rxjs/operators';
import { TreeSet } from 'tstl';
import { isFunction } from 'util';
import { OAuth2Service } from '../../angular-rest';
import { Config } from '../../config.const';

interface IDaySlots {
	day: moment.Moment;
	slots: TreeSet<moment.Moment>;
}

@Component({
	selector: 'app-business-day',
	templateUrl: './business-day.component.html',
	styleUrls: ['./business-day.component.css']
})
export class BusinessDayComponent extends PartitionedEventListenerBase<moment.Moment|number> {

	private static creationRequestId = -1; // FIXME: crearne uno condiviso?

	private hourInterval: moment.Duration;
	private subscription: Subscription;
	public intervalTrees: IDaySlots[];
	public hasIntervals: boolean = true;
	public currentWeek: moment.Moment = moment.utc().startOf('isoWeek').locale('it');

	get height() {
		if (isFunction(Config.defaultCalendarOptions.height)) {
			return (<() => number><any>Config.defaultCalendarOptions.height)();
		} else {
			return Config.defaultCalendarOptions.height;
		}
	}

	constructor(
		route: ActivatedRoute,
		agende: Agende,
		router: Router,
		auther: OAuth2Service,
		private modalService: NgbModal
	) {
		super(route, agende, router, auther);
		moment.locale('it');
	}

	protected getPartitionKeys(appuntamento: IAppuntamento): (moment.Moment|number)[] {

		let eventDay = appuntamento.start.isoWeekday() - 1;

		if (appuntamento.allDay) {
			return [ eventDay ];
		} else {
			const slots = this.intervalTrees[eventDay].slots;

			const result = [ ];
			// Aggiungo tutti gli intervalli da quello trovato a quando l'appuntamento finisce
			// [a, b] overlaps with [x, y] iff b > x and a < y
			for (
				let slot = slots.lower_bound(appuntamento.start);
				!slot.equals(slots.end()) 
					&& slot.value.clone().add(this.hourInterval).isAfter(appuntamento.start)
					&& slot.value.isBefore(appuntamento.end); 
					slot = slot.next()) {
				result.push(slot.value);
			}
			return result;
		}
	}

	protected setup(agenda: Agenda, currentWeek: moment.Moment = moment.utc().startOf('isoWeek')) {

		if (this.subscription) {
			this.subscription.unsubscribe();
		}

		this.subscription = agenda.alternativa.pipe(
			switchMap(week =>
				Observable.combineLatest(...(week.find(s =>
					currentWeek.isSame(moment.utc(s.settimana))
				) || agenda.ambulatorio).map(
					amb =>
						amb.pipe(
							switchMap(pp => pp.length ?
								Observable.combineLatest(...pp) :
								Observable.of([] as IIntervallo[])
							)
						)
				))
			),
			combineLatest(agenda.impostazioni)
		).subscribe(([w, s]) => {
			let hasIntervals = false;
			// FIXME: troppi moment. da cachare.
			this.intervalTrees = w.map((day, weekDay) => ({
				day: currentWeek.clone().add(weekDay, 'day'),
				slots: new TreeSet<moment.Moment>(day.reduce(
					(out, i) => {
						hasIntervals = true;
						out.push(...this.slots(currentWeek.clone().add(weekDay, 'day'), i, s.intervallo))
						return out;
					},
					[] as moment.Moment[]
				), (x, y) => x.isBefore(y))
			}));
			this.hourInterval = moment.duration(s.intervallo);
			this.hasIntervals = hasIntervals;
			super.setup(agenda, currentWeek, currentWeek.clone().add(1, 'week'));
		});

		this.subscription.add(() => {
			this.hourInterval = null;
			this.subscription = null;
			this.intervalTrees = null;
		});
	}

	public slots(day: moment.Moment, intervallo: IIntervallo, slot: NgbTimeStruct) {
		const ret = [] as moment.Moment[];
		day = day.startOf('day');
		let end = day.clone().add(intervallo.fine);
		for (var i = day.clone().add(intervallo.inizio); i.isBefore(end); i = i.clone().add(slot)) {
			ret.push(i);
		}
		ret.push(end);

		// Aggiungo queste proprietà in funzione della visualizzazione.
		ret[0]['isFirstOfGroup'] = true;
		ret[0]['ambulatorio'] = intervallo.ambulatorio;
		ret[ret.length - 1]['isLastOfGroup'] = true;
		return ret;
	}

	public addEvent(start: moment.Moment) {
		return this.editEvent({
			// FIXME: gli id dovrebbero essere numerici dovunque...
			id: (BusinessDayComponent.creationRequestId--).toString(),
			version: 1,
			categoria: null,
			evento: null,
			title: '',
			start: start.clone(),
			end: start.clone().add(this.hourInterval),
			status: 'U'
		});
	}

	public editEvent(event: IAppuntamento) {
		const modalRef = this.modalService.open(NewEventComponent);
		modalRef.componentInstance.settings$ = this.agenda.impostazioni;
		modalRef.componentInstance.setEvento(event);
		modalRef.result.then((event: IAppuntamento | { delete: number } | { delete_group: number }) => {
			if ('delete' in event) {
				this.deleteEvent(event['delete']);
			} else if ('delete_group' in event) {
				this.deleteGroup(event['delete_group']);
			} else {
				if (event.repeat) {
					this.eventRepeatUpsert(event);
				} else {
					this.upsertEvent(event);
				}
			}
		}, () => { });
	}

	async eventRepeatUpsert(event: any) {
		const group_id = await this.agenda.createGroup();
		const version = +event.version + 1;
		let start: moment.Moment = event.start.clone();
		let end: moment.Moment = event.end ? event.end.clone() : null;
		const increment = moment.duration(
			event.repeat.increment,
			<any>event.repeat.incrementUnit
		);
		if (event.repeat.type == 'occorrenze') {
			for (var i = 0; i < event.repeat.incrementNumber; i++) {
				this.upsertEvent({
					group_id,
					id: event.id,
					title: event.title,
					start: start.clone(),
					end: end ? end.clone() : null,
					indirizzo: event.indirizzo,
					numciv: event.numciv,
					citta: event.citta,
					CAP: event.CAP,
					provincia: event.provincia,
					descrizione: event.descrizione,
					categoria: event.categoria,
					evento: event.evento,
					version: version,
					status: event.status
				});
				start = start.add(increment);
				end = end ? end.add(increment) : end;
			}
		} else {
			const until = (event.repeat.incrementUntil as moment.Moment).clone().endOf('day');

			while (start.isSameOrBefore(until)) {
				this.upsertEvent({
					group_id,
					id: event.id,
					title: event.title,
					start: start.clone(),
					end: end ? end.clone() : null,
					indirizzo: event.indirizzo,
					numciv: event.numciv,
					citta: event.citta,
					CAP: event.CAP,
					provincia: event.provincia,
					descrizione: event.descrizione,
					categoria: event.categoria,
					evento: event.evento,
					version: version,
					status: event.status
				});
				start = start.add(increment);
				end = end ? end.add(increment) : end;
			}
		}
	}

	color(event: IAppuntamento) {
		return StyleComponent.sanitize(event.evento) + " rounded bordered";
	}

	nextWeek() {
		this.currentWeek = this.currentWeek.clone().add(1, 'week');
		this.setup(this.agenda, this.currentWeek);
	}

	previousWeek() {
		this.currentWeek = this.currentWeek.clone().subtract(1, 'week');
		this.setup(this.agenda, this.currentWeek);
	}

	today() {
		this.currentWeek = moment.utc().startOf('isoWeek');
		this.setup(this.agenda, this.currentWeek);
	}

	isToday(): boolean {
		return this.currentWeek.isSame(moment.utc().startOf('isoWeek'));
	}
}



// WEBPACK FOOTER //
// ./src/app/calendar/business-day/business-day.component.ts